import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Key from '../clientVariables.json';

// Helper function to determine job status without nested ternary
const determineJobStatus = (job) => {
  if (job.job_verified === 1) {
    return 'valid';
  }
  if (job.job_review_remark && job.job_review_remark.trim() !== '') {
    return 'remarked';
  }
  return 'pending';
};

const JobContext = createContext();

export const JobProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSearchParams, setCurrentSearchParams] = useState(null);

  // Add pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    totalCount: 0
  });
  // Fetch jobs from Solr on component mount
  useEffect(() => {
    const initialFetch = async () => {
      await fetchJobs();
    };
    initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // New function to fetch Prime Vendor jobs
  const fetchJobs = async (searchParams = null) => {
    // Store the current search parameters for pagination
    if (searchParams) {
      setCurrentSearchParams(searchParams);
    }
    
    setIsLoading(true);
    try {
      // Build the Solr query URL based on search parameters or get all jobs
      let searchJobsUrl;

      if (searchParams) {
        // When search parameters are provided
        const { searchQuery, state, city, page, rowsPerPage, status, fromDate, toDate } = searchParams;

        // Calculate start position for pagination
        const start = (page || 0) * (rowsPerPage || 10);        
        // Handle search query properly with quotes and special character handling
        let baseQuery = '*:*';        
        if (searchQuery && searchQuery.trim() !== '') {
          // Clean the query and prepare it for Solr
          const cleanedQuery = searchQuery.trim().replace(/"/g, '');          
          // Check if we need exact matching (for title searches)
          if (searchParams.exactTitleMatch) {
            // Use exact match for title field with properly escaped quotes and no partial matches
            baseQuery = `title:"${cleanedQuery}"`;
            console.log(`Using exact match Solr query: ${baseQuery}`);
            
            // Add highlighting to make it easier to see matches in the UI
            searchJobsUrl += `&hl=true&hl.fl=title&hl.simple.pre=<strong>&hl.simple.post=</strong>`;
          } else {
            // Use more forgiving search patterns with wildcards for partial matches
            baseQuery = `title:*${cleanedQuery}* OR company_name:*${cleanedQuery}* OR ai_skills:*${cleanedQuery}*`;
          }
        }

        // Create the base URL with the search query
        searchJobsUrl = `${Key.Solr_url}/${Key.solrJobs_collection}/select?q=${encodeURIComponent(baseQuery)}`;

        // Add filters for location - only use the fields that exist in schema
        if (state) {
          searchJobsUrl += `&fq=state_name:"${state}"`;

          if (city) {
            searchJobsUrl += `&fq=city_name:"${city}"`;
          }
        }        
        // Add status filter if provided
        if (status && status !== 'all') {
          if (status === 'valid') {
            // Show only jobs with job_verified == 1
            searchJobsUrl += `&fq=job_verified:1`;
          } else if (status === 'remarked') {
            // Show only jobs with job_review_remark that is not empty
            searchJobsUrl += `&fq=job_review_remark:[* TO *]`;
          }
        }

        // Add date range filter if provided
        if (fromDate || toDate) {
          let dateFilter = 'created:[';
          
          // Format the dates for Solr (ISO format)
          if (fromDate) {
            dateFilter += new Date(fromDate).toISOString();
          } else {
            dateFilter += '*';
          }
          
          dateFilter += ' TO ';
          
          if (toDate) {
            // Add one day to include the end date fully
            const endDate = new Date(toDate);
            endDate.setDate(endDate.getDate() + 1);
            dateFilter += endDate.toISOString();
          } else {
            dateFilter += '*';
          }
          
          dateFilter += ']';
          searchJobsUrl += `&fq=${encodeURIComponent(dateFilter)}`;
        }

        // Add status filter
        searchJobsUrl += `&fq=status:(1)`;

        // Add pagination parameters
        searchJobsUrl += `&rows=${rowsPerPage || 10}&start=${start}`;

      } else {
        // Default query for all jobs with pagination
        const start = pagination.page * pagination.rowsPerPage;
        searchJobsUrl = `${Key.Solr_url}/${Key.solrJobs_collection}/select?q=*:*&fq=status:(1)&rows=${pagination.rowsPerPage}&start=${start}`;
      }

      // Add common parameters
      searchJobsUrl += '&sort=created desc&wt=json';

      console.log('Prime Vendor Solr search URL:', searchJobsUrl);
      
      // Make the request to the Solr API via the backend proxy using prime_vendor_jobs endpoint
      const response = await fetch(`${Key.domain}/solr/prime_vendor_jobs`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: searchJobsUrl }),
      });

      const data = await response.json();
      
      // Log exact match information if it was enabled
      if (searchParams?.exactTitleMatch) {
        console.log('Exact match search was used for:', searchParams.searchQuery);
      }
      
      // Log the full response for debugging
      console.log('Full Prime Vendor Solr response:', data);
      
      // Check if we have valid response data
      if (data && data.data && data.data.response && data.data.response.docs) {
        console.log('Prime Vendor Solr response:', data.data.response);

        // Update pagination with total count
        setPagination(prev => ({
          ...prev,
          totalCount: data.data.response.numFound || 0,
          page: searchParams?.page !== undefined ? searchParams.page : prev.page,
          rowsPerPage: searchParams?.rowsPerPage || prev.rowsPerPage
        }));        
        
        // Transform Solr documents to match our expected job format
        const transformedJobs = data.data.response.docs.map(job => {          
          // Format the date properly
          const formattedDate = job.created ? new Date(job.created) : new Date();
          
          // Format specifically for ISO date format with time: YYYY-MM-DD HH:MM:SS
          const dateStr = formattedDate.toISOString().replace('T', ' ').substring(0, 19);
          
          return {
            id: job.id,
            title: job.title || 'Untitled Job',
            company: job.company_name || 'Unknown Company',
            location: {
              state: job.state_name || '',
              city: job.city_name || ''
            },            
            skills: job.ai_skills || [], // Only use ai_skills since prefferdskillsName doesn't exist
            date: dateStr, // Formatted date
            rawDate: job.created || new Date().toISOString(), // Keep original for sorting
            source: job.joblink || '',
            description: job.description || job.details || '',
            job_verified: job.job_verified || 0,
            job_review_remark: job.job_review_remark || '',            
            // Determine status based on job_verified and job_review_remark fields
            status: determineJobStatus(job)
          };
        });
        
        setJobs(transformedJobs);
        console.log('Transformed Prime Vendor jobs:', transformedJobs);
        setFilteredJobs(transformedJobs);
      } else if (data && data.data && data.data.error) {
        // Handle Solr error response
        console.error('Prime Vendor Solr error:', data.data.error);
        setJobs([]);
        setFilteredJobs([]);
      } else {
        setJobs([]);
        setFilteredJobs([]);
        setError('Failed to parse Prime Vendor job data from the server');
      }
    } catch (err) {
      console.error('Error fetching Prime Vendor jobs:', err);
      setTimeout(() => {
        toast.error('Failed to fetch Prime Vendor jobs from the server. Please try again later.');
      }, 1000);
      setError(err.message || 'Failed to fetch Prime Vendor jobs');
    } finally {
      setIsLoading(false);
    }
  };

  // const fetchJobs = async (searchParams = null) => {
  //   // Store the current search parameters for pagination
  //   if (searchParams) {
  //     setCurrentSearchParams(searchParams);
  //   }
    
  //   setIsLoading(true);
  //   try {
  //     // Build the Solr query URL based on search parameters or get all jobs
  //     let searchJobsUrl;

  //     if (searchParams) {
  //       // When search parameters are provided
  //       const { searchQuery, state, city, page, rowsPerPage, status, fromDate, toDate } = searchParams;

  //       // Calculate start position for pagination
  //       const start = (page || 0) * (rowsPerPage || 10);        // Handle search query properly with quotes and special character handling
  //       let baseQuery = '*:*';        if (searchQuery && searchQuery.trim() !== '') {
  //         // Clean the query and prepare it for Solr
  //         const cleanedQuery = searchQuery.trim().replace(/"/g, '');          // Check if we need exact matching (for title searches)
  //         if (searchParams.exactTitleMatch) {
  //           // Use exact match for title field with properly escaped quotes and no partial matches
  //           // We use a phrase query with exact field matching to ensure only exact job titles are returned
  //           baseQuery = `title:"${cleanedQuery}"`;
  //           console.log(`Using exact match Solr query: ${baseQuery}`);
            
  //           // Add highlighting to make it easier to see matches in the UI
  //           searchJobsUrl += `&hl=true&hl.fl=title&hl.simple.pre=<strong>&hl.simple.post=</strong>`;
  //         } else {
  //           // Use more forgiving search patterns with wildcards for partial matches
  //           baseQuery = `title:*${cleanedQuery}* OR company_name:*${cleanedQuery}* OR ai_skills:*${cleanedQuery}*`;
  //         }
  //       }

  //       // Create the base URL with the search query
  //       searchJobsUrl = `${Key.Solr_url}/${Key.solrJobs_collection}/select?q=${encodeURIComponent(baseQuery)}`;

  //       // Add filters for location - only use the fields that exist in schema
  //       if (state) {
  //         searchJobsUrl += `&fq=state_name:"${state}"`;

  //         if (city) {
  //           searchJobsUrl += `&fq=city_name:"${city}"`;
  //         }
  //       }        // Add status filter if provided
  //       if (status && status !== 'all') {
  //         if (status === 'valid') {
  //           // Show only jobs with job_verified == 1
  //           searchJobsUrl += `&fq=job_verified:1`;
  //         } else if (status === 'remarked') {
  //           // Show only jobs with job_review_remark that is not empty
  //           searchJobsUrl += `&fq=job_review_remark:[* TO *]`;
  //         }
  //       }

  //       // Add date range filter if provided
  //       if (fromDate || toDate) {
  //         let dateFilter = 'created:[';
          
  //         // Format the dates for Solr (ISO format)
  //         if (fromDate) {
  //           dateFilter += new Date(fromDate).toISOString();
  //         } else {
  //           dateFilter += '*';
  //         }
          
  //         dateFilter += ' TO ';
          
  //         if (toDate) {
  //           // Add one day to include the end date fully
  //           const endDate = new Date(toDate);
  //           endDate.setDate(endDate.getDate() + 1);
  //           dateFilter += endDate.toISOString();
  //         } else {
  //           dateFilter += '*';
  //         }
          
  //         dateFilter += ']';
  //         searchJobsUrl += `&fq=${encodeURIComponent(dateFilter)}`;
  //       }

  //       // Add industry filter if needed (like in your reference code)
  //       searchJobsUrl += `&fq=status:(1)`;

  //       // Add pagination parameters
  //       searchJobsUrl += `&rows=${rowsPerPage || 10}&start=${start}`;

  //     } else {
  //       // Default query for all jobs with pagination
  //       const start = pagination.page * pagination.rowsPerPage;
  //       searchJobsUrl = `${Key.Solr_url}/${Key.solrJobs_collection}/select?q=*:*&fq=status:(1)&rows=${pagination.rowsPerPage}&start=${start}`;
  //     }

  //     // Add common parameters
  //     searchJobsUrl += '&sort=created desc&wt=json';   
  //     console.log('Solr search URL:', searchJobsUrl);     // Make the request to the Solr API via the backend proxy
  //     // The backend will apply additional filtering to exclude unwanted job sources
  //     const response = await fetch(`${Key.domain}/solr/solr_jobs`, {
  //       method: 'POST',
  //       headers: {
  //         'Accept': 'application/json',
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify({ url: searchJobsUrl }),
  //     });

  //     const data = await response.json();
      
  //     // Log exact match information if it was enabled
  //     if (searchParams?.exactTitleMatch) {
  //       console.log('Exact match search was used for:', searchParams.searchQuery);
  //     }// Log the full response for debugging
  //     console.log('Full Solr response:', data);
      
  //     // Check if we have valid response data
  //     if (data && data.data && data.data.response && data.data.response.docs) {
  //       console.log('Solr response:', data.data.response);

  //       // Update pagination with total count
  //       setPagination(prev => ({
  //         ...prev,
  //         totalCount: data.data.response.numFound || 0,
  //         page: searchParams?.page !== undefined ? searchParams.page : prev.page,
  //         rowsPerPage: searchParams?.rowsPerPage || prev.rowsPerPage
  //       }));        // Transform Solr documents to match our expected job format
  //       const transformedJobs = data.data.response.docs.map(job => {          // Format the date properly
  //         const formattedDate = job.created ? new Date(job.created) : new Date();
          
  //         // Format specifically for ISO date format with time: YYYY-MM-DD HH:MM:SS
  //         const dateStr = formattedDate.toISOString().replace('T', ' ').substring(0, 19);
          
  //         return {
  //           id: job.id,
  //           title: job.title || 'Untitled Job',
  //           company: job.company_name || 'Unknown Company',
  //           location: {
  //             state: job.state_name || '',
  //             city: job.city_name || ''
  //           },            skills: job.ai_skills || [], // Only use ai_skills since prefferdskillsName doesn't exist
  //           date: dateStr, // Formatted date
  //           rawDate: job.created || new Date().toISOString(), // Keep original for sorting
  //           source: job.joblink || '',
  //           description: job.description || job.details || '',
  //           job_verified: job.job_verified || 0,
  //           job_review_remark: job.job_review_remark || '',            // Determine status based on job_verified and job_review_remark fields
  //           status: determineJobStatus(job)
  //         };
  //       });setJobs(transformedJobs);
  //       console.log('Transformed jobs:', transformedJobs);
  //       setFilteredJobs(transformedJobs);
  //     } else if (data && data.data && data.data.error) {
  //       // Handle Solr error response
  //       console.error('Solr error:', data.data.error);
  //       setJobs([]);
  //       setFilteredJobs([]);
  //     } else {
  //       setJobs([]);
  //       setFilteredJobs([]);
  //       setError('Failed to parse job data from the server');
  //     }
  //   } catch (err) {
  //     console.error('Error fetching jobs:', err);
  //     setTimeout(() => {
  //       toast.error('Failed to fetch jobs from the server. Please try again later.');
  //     }, 1000);
  //     setError(err.message || 'Failed to fetch jobs');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
    
    // Use the stored search parameters when changing pages
    fetchJobs({
      ...(currentSearchParams || {}),
      page: newPage,
      rowsPerPage: pagination.rowsPerPage,
      status: activeTab !== 'all' ? activeTab : null
    });
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setPagination(prev => ({
      ...prev,
      page: 0, // Reset to first page
      rowsPerPage: newRowsPerPage
    }));
    
    // Use the stored search parameters when changing rows per page
    fetchJobs({
      ...(currentSearchParams || {}),
      page: 0,
      rowsPerPage: newRowsPerPage,
      status: activeTab !== 'all' ? activeTab : null
    });
  };
  // Function removed as requested by the user
  
  return (
    <JobContext.Provider
      value={{
        jobs,
        filteredJobs,
        setFilteredJobs,
        activeTab,
        setActiveTab,
        selectedJob,
        setSelectedJob,
        isLoading,
        error,
        fetchJobs,
        // fetchPrimeVendorJobs,
        pagination,
        handlePageChange,
        handleChangeRowsPerPage
      }}
    >
      {children}
    </JobContext.Provider>
  );
};

export const useJobContext = () => useContext(JobContext);
